import React from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { LogIn } from "lucide-react";
import {
  isGoogleOAuthConfigured,
  getGoogleClientId,
} from "../../services/googleOAuth";
import { useToast } from "../../contexts/ToastContext";
import { isNativeApp } from "../../platform/native";
import { signInNatively } from "./nativeSignIn";

export function LoginButton({
  className,
  variant = "default",
  size = "default",
  children,
}) {
  const { login, loading, error, isAuthenticated } = useAuth();
  const toast = useToast();

  // Google Login hook - MUST be called at top level, before any early returns
  const googleConfigured = isGoogleOAuthConfigured();
  const googleLogin = useGoogleLogin({
    client_id: getGoogleClientId(),
    ux_mode: "popup",
    // Use implicit flow to get ID token directly in response
    flow: "implicit",
    scope: "openid email profile",
    // This helps prevent COOP errors
    prompt: "select_account",
    onSuccess: async (credentialResponse) => {
      // credentialResponse contains the ID token directly as `credential`
      try {
        // Pass the ID token to the login function
        await login(credentialResponse.credential);
      } catch (err) {
        console.error("Login failed:", err);
      }
    },
    onError: (error) => {
      // Don't show error for user-cancelled login
      if (error?.error !== "popup_closed_by_user") {
        console.error("Google login error:", error);
        toast.showToast({
          title: "Google sign-in failed",
          description:
            "Google could not complete the pop-up sign-in. Please try again.",
          duration: 7000,
        });
      }
    },
    useOneTap: false,
  });

  // Don't show if already authenticated
  if (isAuthenticated) {
    return null;
  }

  const handleClick = () => {
    if (isNativeApp()) {
      signInNatively({ login, toast });
      return;
    }
    try {
      toast.showToast({
        title: "Opening Google sign-in",
        description: "Complete sign-in in the Google window to continue.",
        duration: 3000,
      });
      // Trigger the Google popup - the credential will be passed to onSuccess
      googleLogin();
    } catch (err) {
      console.error("Login failed:", err);
      toast.showToast({
        title: "Sign-in failed",
        description: err.message || "Khronos could not start Google sign-in.",
        duration: 7000,
      });
    }
  };

  return (
    <Button
      onClick={handleClick}
      disabled={loading || !googleConfigured}
      variant={variant}
      size={size}
      className={className}
      title={
        !googleConfigured
          ? "Google Sign-In not configured"
          : "Sign in with Google"
      }
    >
      <LogIn className="mr-2 h-4 w-4" />
      {children ||
        (googleConfigured ? "Sign in with Google" : "Sign-In Unavailable")}
    </Button>
  );
}

export default LoginButton;
